/**
 * Backend error-envelope codes the FRONTEND branches on.
 *
 * The backend answers every failure with `{error: {code, message, ...}}`, where
 * `code` is drawn from `constants/api/error_catalog.py` — a stable, versioned
 * enumeration, unlike `message`, which is prose that changes freely and is not
 * translated. Branching on the code is the only way a component can decide WHAT
 * to show; branching on the prose is how a backend diagnostic ends up rendered
 * verbatim to a paying customer.
 *
 * Only the codes the UI actually reacts to live here. This file is a
 * FRONTEND-side subset by design: adding a code the UI does not handle would
 * publish a contract nothing honours.
 *
 * RULE #1 — one spelling. Both `utils/httpUtils.ts` (which classifies a failed
 * response) and `utils/errorSanitizer.ts` (which picks the message key for a
 * failed job) read these constants, so the two cannot drift apart.
 */

export const API_ERROR_CODES = {
  /**
   * The signed-in account's balance is below the operation's price. HTTP 402.
   * The user's remedy is to buy tokens — see `useTokenGate`.
   */
  INSUFFICIENT_TOKENS: 'INSUFFICIENT_TOKENS',
  /** No credential, or one the server rejected. HTTP 401. Remedy: sign in. */
  UNAUTHORIZED: 'UNAUTHORIZED',
  /** A credential that is valid but stale/expired. HTTP 401. */
  INVALID_TOKEN: 'INVALID_TOKEN',
  /** Expired credential. HTTP 401. */
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  /** Too many requests. HTTP 429. */
  RATE_LIMITED: 'RATE_LIMITED',
  /**
   * The chosen provider needs a third-party API key this installation does not
   * hold. HTTP 400.
   *
   * For image generation this is an ADVANCED-user outcome, not the ordinary
   * one: a signed-in customer without their own key is served by the operator's
   * key and billed for it, so the ordinary refusal is INSUFFICIENT_TOKENS. The
   * code is listed here so the UI can tell the two apart rather than showing
   * "set your API key" to everyone who runs out of balance.
   */
  PROVIDER_NOT_CONFIGURED: 'PROVIDER_NOT_CONFIGURED',
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

/**
 * HTTP statuses the UI branches on when the envelope carries no `error.code`.
 *
 * Both backends derive the code FROM the status for raw HTTPExceptions
 * (`shared/middleware/error_handler.py::http_status_to_error_code`), so the two
 * signals agree — but a handful of routes still answer 402/429 with no code at
 * all, and a status-only check is what keeps those from falling through to the
 * generic "something went wrong".
 */
export const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  PAYMENT_REQUIRED: 402,
  TOO_MANY_REQUESTS: 429,
} as const;
